import { useState } from "react";
import { toast } from "sonner";
import { useScanners } from "@/hooks/useScanners";
import { IntegrationConfigDialog } from "@/components/IntegrationConfigDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Scan, 
  Cloud, 
  Shield, 
  AlertTriangle, 
  CheckCircle,
  Settings,
  Play,
  Info,
  Plus
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TranslatedText } from "@/components/TranslatedText";

export default function ScannersPage() {
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const { integrations, scans, findings, runScan, refetch, loading, error, connectIntegration, deleteIntegration, deleteFinding, addFindingToTasks } = useScanners();

  const handleRunAll = async () => {
    if (!integrations || integrations.length === 0) {
      toast.error("No integrations connected");
      return;
    }
    try {
      await Promise.all(integrations.map((i) => runScan(i.id)));
      toast.success("Scans started for all integrations");
    } catch (e: any) {
      console.error("Run all error:", e);
      toast.error(e?.message || "Failed to start scans");
    }
  };

  const handleRunScan = async (integrationId: string) => {
    try {
      await runScan(integrationId);
      toast.success("Scan started");
    } catch (e: any) {
      toast.error(e?.message || "Failed to start scan");
    }
  };

  const handleDelete = async (integrationId: string) => {
    if (!confirm('Are you sure you want to delete this integration?')) return;
    try {
      await deleteIntegration(integrationId);
      toast.success('Integration deleted');
      refetch();
    } catch (e: any) {
      console.error('Delete error:', e);
      toast.error(e?.message || 'Delete failed');
    }
  };

  const handleDeleteFinding = async (findingId: string) => {
    if (!confirm('Are you sure you want to delete this finding?')) return;
    try {
      await deleteFinding(findingId);
      toast.success('Finding deleted');
    } catch (e: any) {
      console.error('Delete finding error:', e);
      toast.error(e?.message || 'Delete failed');
    }
  };

  const handleAddToTasks = async (finding: any) => {
    try {
      await addFindingToTasks(finding);
      toast.success('Finding added to Tasks');
    } catch (e: any) {
      console.error('Add to tasks error:', e);
      toast.error(e?.message || 'Failed to add to Tasks');
    }
  };

  const getIntegrationIcon = (type: string) => {
    return <Cloud className="h-5 w-5" />;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { variant: "default" as const, text: "Active" },
      inactive: { variant: "secondary" as const, text: "Inactive" },
      error: { variant: "destructive" as const, text: "Error" },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getSeverityBadge = (severity: string) => {
    const severityConfig = {
      critical: { variant: "destructive" as const, text: "Critical" },
      high: { variant: "destructive" as const, text: "High" },
      medium: { variant: "secondary" as const, text: "Medium" },
      low: { variant: "outline" as const, text: "Low" },
    };
    const config = severityConfig[severity as keyof typeof severityConfig] || severityConfig.low;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">AI Security Scanners</h1>
          <p className="text-muted-foreground">
            Automated security scans of cloud infrastructure with AI
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setConfigDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Integration
          </Button>
          {integrations && integrations.length > 0 && (
            <Button onClick={handleRunAll} disabled={loading}>
              <Scan className="h-4 w-4 mr-2" />
              Scan All
            </Button>
          )}
        </div>
      </div>

      {/* How it works */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>How it works</AlertTitle>
        <AlertDescription className="space-y-2 text-sm">
          <p>
            <strong>1. Connect your cloud platform:</strong> Click "New Integration" and add your AWS, Azure, Google Cloud, GitHub, Okta or Hetzner account with API credentials.
          </p>
          <p>
            <strong>2. Start a scan:</strong> Our AI automatically scans your infrastructure for security vulnerabilities, misconfigurations, and compliance issues.
          </p>
          <p>
            <strong>3. Review findings:</strong> Get instant insight into detected security issues with severity ratings (critical, high, medium, low) and remediation recommendations.
          </p>
        </AlertDescription>
      </Alert>

      {/* Empty state when no integrations */}
      {(!integrations || integrations.length === 0) && !loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <Cloud className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
              <div>
                <h3 className="text-lg font-semibold mb-2">No integrations connected</h3>
                <p className="text-muted-foreground mb-4">
                  Get started by adding your first cloud platform or service integration.
                </p>
                <Button onClick={() => setConfigDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Integration
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integrations List */}
      {integrations && integrations.length > 0 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Connected Integrations</CardTitle>
              <CardDescription>
                {integrations.length} {integrations.length === 1 ? 'platform' : 'platforms'} connected
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {integrations.map((integration) => (
                  <div key={integration.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {getIntegrationIcon(integration.type)}
                        <div className="flex-1">
                          <h4 className="font-medium">{integration.name}</h4>
                          <p className="text-sm text-muted-foreground capitalize">
            {integration.type}
            {integration.last_sync && ` • Last sync: ${new Date(integration.last_sync).toLocaleString('en-US')}`}
                          </p>
                        </div>
                        {getStatusBadge(integration.status)}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRunScan(integration.id)}
                          disabled={loading}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Scan
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" aria-label="Settings">
                              <Settings className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-50">
            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(integration.id)}>
              Delete
            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Scans */}
          {scans && scans.length > 0 && (
            <Card>
              <CardHeader>
              <CardTitle>Recent Scans</CardTitle>
              <CardDescription>
                Latest {scans.length} scan{scans.length !== 1 ? 's' : ''}
              </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {scans.slice(0, 5).map((scan) => {
                    const integration = integrations.find(i => i.id === scan.integration_id);
                    return (
                      <div key={scan.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          {scan.status === 'running' && <Scan className="h-4 w-4 animate-spin text-blue-500" />}
                          {scan.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {scan.status === 'failed' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                          <div>
                            <p className="text-sm font-medium">
                              {integration?.name || 'Unknown integration'}
                            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(scan.created_at).toLocaleString('en-US')}
            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {scan.findings_count > 0 && (
                            <Badge variant="secondary">
                              {scan.findings_count} finding{scan.findings_count !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          <Badge variant="outline" className="capitalize">
                            {scan.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Findings */}
          {findings && findings.length > 0 && (
            <Card>
              <CardHeader>
              <CardTitle>Security Findings</CardTitle>
              <CardDescription>
                {findings.length} finding{findings.length !== 1 ? 's' : ''} found
              </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {findings.slice(0, 10).map((finding) => (
                    <div key={finding.id} className="border rounded-lg p-4 hover:bg-muted/50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                            <h4 className="font-medium"><TranslatedText text={finding.title} /></h4>
                            {getSeverityBadge(finding.severity)}
                            <Badge variant={finding.status === 'open' ? 'destructive' : 'outline'} className="capitalize">
                              {finding.status}
                            </Badge>
                          </div>
                          {finding.description && (
                            <p className="text-sm text-muted-foreground"><TranslatedText text={finding.description} /></p>
                          )}
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                              Resource: {finding.resource_ref}
                            </p>
                            {finding.scan && (
                              <p className="text-xs text-muted-foreground">
            Scan: {finding.scan.integration?.name || 'Unknown'} • 
            Found on: {new Date(finding.created_at).toLocaleDateString('en-US')} at {new Date(finding.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleAddToTasks(finding)}
                          >
            <Plus className="h-3 w-3 mr-1" />
            Add to Tasks
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" aria-label="Options">
                                <Settings className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="z-50">
            <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteFinding(finding.id)}>
              Delete
            </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <IntegrationConfigDialog 
        open={configDialogOpen} 
        onOpenChange={(open) => {
          setConfigDialogOpen(open);
          if (!open) {
            refetch();
          }
        }} 
      />
    </div>
  );
}